import {
  CalendarPlus,
  Crop,
  FileText,
  Loader2,
  MessageCirclePlus,
  RotateCw,
  ScanText,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { motion } from "motion/react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { OrganizationFeatures } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { DeletePageDialog } from "~/features/chat/components/modals/DeletePageDialog";

type SelectedPagePanelProps = {
  isViewOnly: boolean;
  unselectPages: () => void;
  onRotateClicked: () => void;
  onDeleteClicked: () => void;
  onRecognizeClicked?: () => void;
  onZoomClicked?: () => void;
  onAddNoteClicked?: () => void;
  onHighlightClicked?: () => void;
  onCropClicked?: () => void;
  onAddToChronologyClicked?: () => void;
  selectedPageNumber?: number;
  isRotateLoading: boolean;
  isRecognizeLoading?: boolean;
  isZoomActive?: boolean;
  isAddingAnnotation?: boolean;
  isHighlightActive?: boolean;
  isCropActive?: boolean;
  features: OrganizationFeatures;
};

export const SelectedPagePanel: FC<SelectedPagePanelProps> = ({
  isViewOnly,
  unselectPages,
  onRotateClicked,
  isRotateLoading,
  onDeleteClicked,
  onRecognizeClicked,
  onZoomClicked,
  onAddNoteClicked,
  onHighlightClicked,
  onAddToChronologyClicked,
  selectedPageNumber,
  isRecognizeLoading = false,
  isZoomActive = false,
  isAddingAnnotation = false,
  isHighlightActive = false,
  onCropClicked,
  isCropActive = false,
  features = {},
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{
          y: 0,
          opacity: 1,
          transition: { duration: 0.2, ease: "easeOut" },
        }}
        exit={{
          y: 20,
          opacity: 0,
          transition: { duration: 0.15, ease: "easeIn" },
        }}
        className="h-14 px-4 py-2 pr-3 rounded-xl shadow-[0_-4px_20px_rgba(59,130,246,0.3)] flex justify-center items-center bg-blue-600 text-white"
      >
        <div className="flex items-center gap-1">
          <div className="pr-2 flex items-center gap-5">
            <div className="text-white text-sm font-normal min-w-[100px]">
              {selectedPageNumber ? (
                <FormattedMessage
                  id="documentsList.pageSelected"
                  defaultMessage="Page {number} selected"
                  values={{
                    number: selectedPageNumber,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="documentsList.pageSelected"
                  defaultMessage="Page selected"
                />
              )}
            </div>
          </div>
          {onRecognizeClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto"
                  onClick={onRecognizeClicked}
                  disabled={isRecognizeLoading}
                >
                  {isRecognizeLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.recognize" />
              </TooltipContent>
            </Tooltip>
          )}
          {onHighlightClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="selected-page-highlight-toggle"
                  variant="outline"
                  size="icon"
                  className={`bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto ${isHighlightActive ? "ring-2 ring-white" : ""}`}
                  onClick={onHighlightClicked}
                  disabled={isZoomActive}
                >
                  <ScanText className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage
                  id="documentsList.highlight"
                  defaultMessage="Highlight"
                />
              </TooltipContent>
            </Tooltip>
          )}
          {onZoomClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto"
                  onClick={onZoomClicked}
                >
                  {isZoomActive ? (
                    <ZoomOut className="w-4 h-4" />
                  ) : (
                    <ZoomIn className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.zoom" />
              </TooltipContent>
            </Tooltip>
          )}
          {onCropClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="selected-page-crop-toggle"
                  variant="outline"
                  size="icon"
                  className={`bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto ${isCropActive ? "ring-2 ring-white" : ""}`}
                  onClick={onCropClicked}
                  disabled={isZoomActive}
                >
                  <Crop className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage
                  id="documentsList.crop"
                  defaultMessage="Crop to Copy"
                />
              </TooltipContent>
            </Tooltip>
          )}
          {!isViewOnly && onAddNoteClicked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={`bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto ${isAddingAnnotation ? "ring-2 ring-white" : ""}`}
                  onClick={onAddNoteClicked}
                  disabled={isZoomActive}
                >
                  <MessageCirclePlus className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage
                  id="pageAnnotations.addNote"
                  defaultMessage="Add Note"
                />
              </TooltipContent>
            </Tooltip>
          )}
          {!isViewOnly &&
            onAddToChronologyClicked &&
            features.useChronology && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="selected-page-add-to-chronology"
                    variant="outline"
                    size="icon"
                    className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto"
                    onClick={onAddToChronologyClicked}
                    disabled={isZoomActive}
                  >
                    <CalendarPlus className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <FormattedMessage
                    id="docViewer.addToChronology"
                    defaultMessage="Add to Chronology"
                  />
                </TooltipContent>
              </Tooltip>
            )}
          {!isViewOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto"
                  onClick={onRotateClicked}
                  disabled={isRotateLoading || isZoomActive}
                >
                  {isRotateLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCw className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.rotate" />
              </TooltipContent>
            </Tooltip>
          )}
          {!isViewOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! pointer-events-auto"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isZoomActive}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage id="documentsList.delete" />
              </TooltipContent>
            </Tooltip>
          )}
          <Button
            variant="outline"
            size="icon"
            className="bg-brand hover:bg-blue-700 border-transparent text-brand-foreground! pointer-events-auto"
            onClick={unselectPages}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
      <DeletePageDialog
        open={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onDelete={() => {
          onDeleteClicked();
          setIsDeleteDialogOpen(false);
        }}
        selectedCount={1}
      />
    </>
  );
};
